// kernel/useZoom.ts
import { useRef, useCallback } from 'react';
import { KernelHandle, useZoomReturnType, Rect } from  '#types/types';
import {
  getProjectPanOffset,
  getProjectZoomLevel,
  setProjectPanOffset,
  setProjectZoomLevel,
} from '#lib/project-runtime-layer-ui-state';
import { STUDIO_LIVE_VIEWPORT_VARS, STUDIO_VIEWPORT_TRANSFORM_EVENT, VIEWPORT_TRANSFORM_CONTAINER_SELECTOR, getTimelineDockBottomInsetPx } from '#lib/studio-viewport';
import { clampPanToArtboard } from '#lib/studio-artboard';

// A linear 1 -> 1.4 scale tween (one wheel-zoom step) is perceived logarithmically; this is that curve, reused so panning feels like zooming.
const WHEEL_ZOOM_PERCEIVED_EASING = (progress: number) => Math.log(1 + 0.4 * progress) / Math.log(1.4);

interface useZoomProps {
  kernel: React.RefObject<KernelHandle | null>;
}

export const useZoom = ({
  kernel
}: useZoomProps) : useZoomReturnType => {
  const animationFrameRef = useRef<number | null>(null);
  const viewportElementRef = useRef<HTMLDivElement | null>(null);
  const renderedZoomRef = useRef<number | null>(null);
  const renderedPanRef = useRef<{ x: number; y: number } | null>(null);
  const animationStartTimeRef = useRef<number | null>(null);
  const animationTargetZoomLevelRef = useRef<number | null>(null);
  const animationStartZoomLevelRef = useRef<number | null>(null);
  const animationStartPanOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const animationTargetPanOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const panStoreCommitFrameRef = useRef<number | null>(null);

  const resolveViewportElement = useCallback(() => {
    const canvasContainer = kernel.current?.canvasContainerRef.current;
    if (!canvasContainer) {
      viewportElementRef.current = null;
      return null;
    }

    const cached = viewportElementRef.current;
    if (cached && canvasContainer.contains(cached)) {
      return cached;
    }

    const found = canvasContainer.querySelector<HTMLDivElement>(VIEWPORT_TRANSFORM_CONTAINER_SELECTOR);
    viewportElementRef.current = found;
    return found;
  }, [kernel]);

  const applyViewportTransform = useCallback((pan: { x: number; y: number }, zoom: number, syncCssVars: boolean = true) => {
    const viewportElement = resolveViewportElement();
    if (!viewportElement) return;
    const safeZoom = Math.max(zoom, 0.0001);
    viewportElement.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
    const canvasContainer = kernel.current?.canvasContainerRef.current;
    if (canvasContainer) {
      canvasContainer.style.setProperty(STUDIO_LIVE_VIEWPORT_VARS.panX, `${pan.x}px`);
      canvasContainer.style.setProperty(STUDIO_LIVE_VIEWPORT_VARS.panY, `${pan.y}px`);
      canvasContainer.style.setProperty(STUDIO_LIVE_VIEWPORT_VARS.zoom, String(safeZoom));
    }
    if (syncCssVars) {
      viewportElement.style.setProperty('--zoom-level', String(safeZoom));
      viewportElement.style.setProperty('--inverse-zoom-level', String(1 / safeZoom));
    }
    renderedZoomRef.current = zoom;
    renderedPanRef.current = { x: pan.x, y: pan.y };
    // Synchronous, after every style write: a layer drawn by script (components/LayerWebGLRaster.tsx) redraws for
    // this transform before the frame is painted, instead of lagging one frame behind the scene.
    viewportElement.dispatchEvent(new Event(STUDIO_VIEWPORT_TRANSFORM_EVENT));
  }, [resolveViewportElement]);

  const getRenderedViewportState = useCallback((projectIdInput: string) => {
    if (!kernel.current) {
      return { zoom: 1, pan: { x: 0, y: 0 } };
    }
    const projectId = projectIdInput.trim();
    const zoom = renderedZoomRef.current ?? (projectId ? getProjectZoomLevel(kernel.current, projectId) : 1);
    const pan = renderedPanRef.current ?? getProjectPanOffset(kernel.current, projectId);
    return { zoom, pan };
  }, [kernel]);

  const getZoomIntentBaseState = useCallback((projectIdInput: string) => {
    const activeTargetZoom = animationTargetZoomLevelRef.current;
    const activeTargetPan = animationTargetPanOffsetRef.current;
    if (activeTargetZoom !== null && activeTargetPan !== null) {
      // Cumulate incoming wheel events on the in-flight intent target.
      return { zoom: activeTargetZoom, pan: activeTargetPan };
    }
    return getRenderedViewportState(projectIdInput);
  }, [getRenderedViewportState]);

  const computeZoomToFitDurationMs = useCallback(
    (
      startZoom: number,
      startPan: { x: number; y: number },
      targetZoom: number,
      targetPan: { x: number; y: number },
      viewportSize: { width: number; height: number }
    ) => {
      const MIN_DURATION_MS = 180;
      const MAX_DURATION_MS = 520;
      const BASE_DURATION_MS = 190;
      const ZOOM_STEP_FACTOR = 1.4; // Same semantic step as wheel zoom.
      const ZOOM_UNIT_MS = 34;
      const PAN_UNIT_MS = 72;

      const safeStartZoom = Math.max(startZoom, 0.0001);
      const safeTargetZoom = Math.max(targetZoom, 0.0001);
      const zoomDistance = Math.abs(Math.log(safeTargetZoom / safeStartZoom));
      const zoomUnits = zoomDistance / Math.log(ZOOM_STEP_FACTOR);

      const panDistancePx = Math.hypot(targetPan.x - startPan.x, targetPan.y - startPan.y);
      const viewportDiagonal = Math.max(1, Math.hypot(viewportSize.width, viewportSize.height));
      const panUnits = Math.min(2.5, panDistancePx / viewportDiagonal);

      const duration =
        BASE_DURATION_MS +
        zoomUnits * ZOOM_UNIT_MS +
        panUnits * PAN_UNIT_MS;

      return Math.round(Math.min(MAX_DURATION_MS, Math.max(MIN_DURATION_MS, duration)));
    },
    []
  );

  // A Vector project is a fixed artboard: its frame never leaves the window. Other kinds pan freely.
  const clampViewportPanForProject = useCallback<useZoomReturnType['clampViewportPanForProject']>((projectIdInput, pan, zoom) => {
    const k = kernel.current;
    const projectId = projectIdInput.trim();
    // Test harnesses mount this hook on a partial kernel; a kernel that cannot say the kind pans freely.
    if (!k || !projectId || typeof k.getInventory !== 'function' || k.getInventory().Project?.registry[projectId]?.projectType !== 'VECTOR') return pan;
    const container = k.canvasContainerRef?.current;
    const workspaceId = k.getProjectUIWorkspaceId?.(projectId);
    const artboard = workspaceId ? k.getWorkspace?.(workspaceId)?.frameRect : undefined;
    if (!container || !artboard) return pan;
    return clampPanToArtboard(pan, zoom, artboard, { width: container.clientWidth, height: container.clientHeight });
  }, [kernel]);

  const animateZoomAndPan = useCallback(
    (projectIdInput: string, targetZoom: number, targetPan: { x: number; y: number }, animationDuration: number, easing: (progress: number) => number = (progress) => progress) => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (!kernel.current) return;
      const projectId = projectIdInput.trim();
      if (!projectId) return;

      animationTargetZoomLevelRef.current = targetZoom;
      const renderedState = getRenderedViewportState(projectId);
      animationStartZoomLevelRef.current = renderedState.zoom;
      animationStartTimeRef.current = performance.now();
      animationStartPanOffsetRef.current = renderedState.pan;
      animationTargetPanOffsetRef.current = clampViewportPanForProject(projectId, targetPan, targetZoom);

      const animate = () => {
        const now = performance.now();
        if (
          animationStartTimeRef.current === null ||
          animationStartZoomLevelRef.current === null ||
          animationTargetZoomLevelRef.current === null ||
          animationStartPanOffsetRef.current === null ||
          animationTargetPanOffsetRef.current === null
        ) {
          return;
        }

        const elapsedTime = now - animationStartTimeRef.current;
        const progress = Math.min(1, elapsedTime / animationDuration);
        const easedProgress = easing(progress);

        const newZoom =
          animationStartZoomLevelRef.current +
          (animationTargetZoomLevelRef.current -
            animationStartZoomLevelRef.current) *
            easedProgress;
        const newPanX =
          animationStartPanOffsetRef.current.x +
          (animationTargetPanOffsetRef.current.x -
            animationStartPanOffsetRef.current.x) *
            easedProgress;
        const newPanY =
          animationStartPanOffsetRef.current.y +
          (animationTargetPanOffsetRef.current.y -
            animationStartPanOffsetRef.current.y) *
            easedProgress;

        applyViewportTransform({ x: newPanX, y: newPanY }, newZoom, false);

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          const runtimeKernel = kernel.current;
          if (!runtimeKernel) {
            animationFrameRef.current = null;
            animationStartTimeRef.current = null;
            animationTargetZoomLevelRef.current = null;
            animationStartZoomLevelRef.current = null;
            animationStartPanOffsetRef.current = null;
            animationTargetPanOffsetRef.current = null;
            return;
          }
          // Read from the ref, not the closure: shiftViewportPan may have moved the target mid-flight.
          const finalPan = animationTargetPanOffsetRef.current;
          applyViewportTransform(finalPan, targetZoom, true);
          setProjectZoomLevel(runtimeKernel, projectId, targetZoom);
          setProjectPanOffset(runtimeKernel, projectId, { x: finalPan.x, y: finalPan.y });
          animationFrameRef.current = null;
          animationStartTimeRef.current = null;
          animationTargetZoomLevelRef.current = null;
          animationStartZoomLevelRef.current = null;
          animationStartPanOffsetRef.current = null;
          animationTargetPanOffsetRef.current = null;
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    },
    [kernel, applyViewportTransform, getRenderedViewportState, clampViewportPanForProject]
  );

  const handleZoom = useCallback(
    (
      projectIdInput: string,
      event: React.WheelEvent<HTMLDivElement>,
      canvasContainer: HTMLDivElement | null
    ) => {
      if (!canvasContainer) return;
      if (!kernel.current) return;
      const projectId = projectIdInput.trim();
      if (!projectId) return;
      const rect = canvasContainer.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      const zoomFactor = event.deltaY < 0 ? 1.4 : 1 / 1.4;
      const intentState = getZoomIntentBaseState(projectId);
      const currentZoom = intentState.zoom;
      const targetZoomLevel = Math.min(4096.0, Math.max(0.004, currentZoom * zoomFactor));

      const zoomPointX = mouseX;
      const zoomPointY = mouseY;

      const currentPan = intentState.pan;
      const currentPanX = currentPan.x;
      const currentPanY = currentPan.y;

      const targetPanX = zoomPointX - (zoomPointX - currentPanX) * (targetZoomLevel / currentZoom);
      const targetPanY = zoomPointY - (zoomPointY - currentPanY) * (targetZoomLevel / currentZoom);

      if (kernel.current && event.deltaY > 0 && Math.abs(targetZoomLevel - currentZoom) < 0.0001) {
        kernel.current.zoomOnAllFrames(projectId);
      } else {
        animateZoomAndPan(projectId, targetZoomLevel, { x: targetPanX, y: targetPanY }, 160);
      }
    },
    [animateZoomAndPan, kernel, getZoomIntentBaseState]
  );

  const handleWheelPan = useCallback(
    (projectIdInput: string, deltaX: number, deltaY: number) => {
      if (!kernel.current) return;
      const projectId = projectIdInput.trim();
      if (!projectId) return;
      const intentState = getZoomIntentBaseState(projectId);
      const targetPan = clampViewportPanForProject(projectId, { x: intentState.pan.x - deltaX, y: intentState.pan.y - deltaY }, intentState.zoom);
      // Same threshold as wheel zoom: small deltas are a trackpad stream, which animating would only make lag.
      if (Math.abs(deltaX) + Math.abs(deltaY) > 25) {
        animateZoomAndPan(projectId, intentState.zoom, targetPan, 140, WHEEL_ZOOM_PERCEIVED_EASING);
        return;
      }
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      animationStartTimeRef.current = null;
      animationTargetZoomLevelRef.current = null;
      animationStartZoomLevelRef.current = null;
      animationStartPanOffsetRef.current = null;
      animationTargetPanOffsetRef.current = null;
      applyViewportTransform(targetPan, intentState.zoom);
      setProjectZoomLevel(kernel.current, projectId, intentState.zoom);
      setProjectPanOffset(kernel.current, projectId, targetPan);
    },
    [kernel, animateZoomAndPan, applyViewportTransform, getZoomIntentBaseState, clampViewportPanForProject]
  );

  const shiftViewportPan = useCallback(
    (projectIdInput: string, dx: number, dy: number) => {
      if (!kernel.current) return;
      const projectId = projectIdInput.trim();
      if (!projectId) return;
      const startPan = animationStartPanOffsetRef.current;
      const targetPan = animationTargetPanOffsetRef.current;
      if (animationFrameRef.current !== null && startPan && targetPan) {
        animationStartPanOffsetRef.current = { x: startPan.x + dx, y: startPan.y + dy };
        animationTargetPanOffsetRef.current = { x: targetPan.x + dx, y: targetPan.y + dy };
        return;
      }
      const { zoom, pan } = getRenderedViewportState(projectId);
      applyViewportTransform({ x: pan.x + dx, y: pan.y + dy }, zoom);
      // Called from a ResizeObserver, i.e. mid-frame: a store write there makes React flush synchronously, which also
      // commits the panel's pending width and moves the container again after this compensation, one frame of lag.
      if (panStoreCommitFrameRef.current !== null) return;
      panStoreCommitFrameRef.current = requestAnimationFrame(() => {
        panStoreCommitFrameRef.current = null;
        const runtimeKernel = kernel.current;
        const renderedPan = renderedPanRef.current;
        if (!runtimeKernel || !renderedPan) return;
        // The compensation itself is never clamped (it would jitter); the artboard is pulled back once it settles.
        const settledPan = clampViewportPanForProject(projectId, renderedPan, renderedZoomRef.current ?? getProjectZoomLevel(runtimeKernel, projectId));
        if (settledPan.x !== renderedPan.x || settledPan.y !== renderedPan.y) applyViewportTransform(settledPan, renderedZoomRef.current ?? getProjectZoomLevel(runtimeKernel, projectId));
        setProjectPanOffset(runtimeKernel, projectId, settledPan);
      });
    },
    [kernel, applyViewportTransform, getRenderedViewportState, clampViewportPanForProject]
  );

  const zoomToFitRectInViewport = useCallback(
    (projectIdInput: string, rectToFit: Rect, marginFactorMultiplier: number = 1.1, spectacleMode: boolean = false) => {
      if (!kernel.current?.canvasContainerRef?.current || !rectToFit) {
        console.warn("zoomToFitRectInViewport: Missing canvas container ref or rectToFit. Cannot perform zoom.");
        return;
      }
      const projectId = projectIdInput.trim();
      if (!projectId) return;

      const canvasContainer = kernel.current.canvasContainerRef.current;
      const containerWidth = canvasContainer.offsetWidth;
      const containerHeight = canvasContainer.offsetHeight - getTimelineDockBottomInsetPx(canvasContainer);

      if (rectToFit.w <= 0 || rectToFit.h <= 0 || containerWidth <= 0 || containerHeight <= 0) {
        console.warn(
          `zoomToFitRectInViewport: Invalid dimensions for rectToFit (w:${rectToFit.w}, h:${rectToFit.h}) or container (w:${containerWidth}, h:${containerHeight}). Cannot perform zoom.`
        );
        return;
      }
      
      // Ensure marginFactorMultiplier is positive; if not, default to a safe value (e.g., 1.1 for 10% margin)
      const safeMarginFactor = (typeof marginFactorMultiplier === 'number' && marginFactorMultiplier > 0) 
                                ? marginFactorMultiplier 
                                : 1.1;

      const effectiveContentWidth = rectToFit.w * safeMarginFactor;
      const effectiveContentHeight = rectToFit.h * safeMarginFactor;

      if (effectiveContentWidth <= 0 || effectiveContentHeight <= 0) {
          console.warn("zoomToFitRectInViewport: Effective content dimensions are non-positive after applying marginFactor. Aborting zoom.");
          return;
      }
      const targetZoomX = containerWidth / effectiveContentWidth;
      const targetZoomY = containerHeight / effectiveContentHeight;
      const targetZoomLevel = Math.min(targetZoomX, targetZoomY);
      const clampedTargetZoomLevel = Math.min(32, Math.max(0.01, targetZoomLevel));
      const rectCenterXWorld = rectToFit.x + rectToFit.w / 2;
      const rectCenterYWorld = rectToFit.y + rectToFit.h / 2;
      const targetPanX = (containerWidth / 2) - (rectCenterXWorld * clampedTargetZoomLevel);
      const targetPanY = (containerHeight / 2) - (rectCenterYWorld * clampedTargetZoomLevel);
      const renderedState = getRenderedViewportState(projectId);
      const animationDuration = computeZoomToFitDurationMs(
        renderedState.zoom,
        renderedState.pan,
        clampedTargetZoomLevel,
        { x: targetPanX, y: targetPanY },
        { width: containerWidth, height: containerHeight }
      );

      animateZoomAndPan(projectId, clampedTargetZoomLevel, { x: targetPanX, y: targetPanY }, animationDuration);
    },
    [kernel, animateZoomAndPan, getRenderedViewportState, computeZoomToFitDurationMs]
  );

  const handleZoomInFromButton = useCallback((projectIdInput: string) => {
    if (!kernel.current?.canvasContainerRef.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;
    
    const intentState = getZoomIntentBaseState(projectId);
    const currentZoom = intentState.zoom;
    const currentPan = intentState.pan;
    const newZoom = Math.min(4096.0, currentZoom * 1.6);
  
    const canvasContainer = kernel.current.canvasContainerRef.current;
    const rect = canvasContainer.getBoundingClientRect();
    const centerX = rect.width / 2; // Zoom towards center of viewport
    const centerY = rect.height / 2;
  
    const newPanX = centerX - (centerX - currentPan.x) * (newZoom / currentZoom);
    const newPanY = centerY - (centerY - currentPan.y) * (newZoom / currentZoom);
  
    animateZoomAndPan(projectId, newZoom, { x: newPanX, y: newPanY }, 160);
  }, [kernel, animateZoomAndPan, getZoomIntentBaseState]);
  
  const handleZoomOutFromButton = useCallback((projectIdInput: string) => {
    if (!kernel.current?.canvasContainerRef.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;
  
    const intentState = getZoomIntentBaseState(projectId);
    const currentZoom = intentState.zoom;
    const currentPan = intentState.pan;
    const newZoom = Math.max(0.004, currentZoom / 1.6);
  
    const canvasContainer = kernel.current.canvasContainerRef.current;
    const rect = canvasContainer.getBoundingClientRect();
    const centerX = rect.width / 2; // Zoom towards center of viewport
    const centerY = rect.height / 2;
  
    const newPanX = centerX - (centerX - currentPan.x) * (newZoom / currentZoom);
    const newPanY = centerY - (centerY - currentPan.y) * (newZoom / currentZoom);
  
    animateZoomAndPan(projectId, newZoom, { x: newPanX, y: newPanY }, 160);
  }, [kernel, animateZoomAndPan, getZoomIntentBaseState]);

  return { animateZoomAndPan, handleZoom, handleWheelPan, shiftViewportPan, zoomToFitRectInViewport, handleZoomInFromButton, handleZoomOutFromButton, applyViewportTransform, clampViewportPanForProject };
};

